/**
 * SearchStatsDashboard 组件
 *
 * 统计仪表板标签页，整合供应商分布、错误统计和延迟统计。
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  searchAdminApi,
  type SearchProviderStats,
  type SearchErrorItem,
  type SearchLatencyItem,
} from '../../../services/searchAdminApi';
import { ProviderDistribution } from './ProviderDistribution';
import { ErrorBreakdown } from './ErrorBreakdown';
import { LatencyStats } from './LatencyStats';

interface SearchStatsDashboardProps {
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export const SearchStatsDashboard: React.FC<SearchStatsDashboardProps> = ({
  autoRefresh = true,
  refreshInterval = 30000,
}) => {
  const [providers, setProviders] = useState<SearchProviderStats[]>([]);
  const [errors, setErrors] = useState<SearchErrorItem[]>([]);
  const [latency, setLatency] = useState<SearchLatencyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchStats = useCallback(async () => {
    try {
      const [p, e, l] = await Promise.all([
        searchAdminApi.getProviderStats(),
        searchAdminApi.getErrorStats(),
        searchAdminApi.getLatencyStats(),
      ]);
      setProviders(p.data);
      setErrors(e.data);
      setLatency(l.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取统计数据失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    if (!autoRefresh) return;
    const interval = setInterval(fetchStats, refreshInterval);
    return () => clearInterval(interval);
  }, [fetchStats, autoRefresh, refreshInterval]);

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
        {error}
        <button
          onClick={fetchStats}
          className="ml-3 text-red-500 hover:text-red-700 underline"
        >
          重试
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ProviderDistribution providers={providers} loading={loading} />
      <ErrorBreakdown errors={errors} loading={loading} />
      <LatencyStats data={latency} loading={loading} />
    </div>
  );
};

export { ProviderDistribution } from './ProviderDistribution';
export { ErrorBreakdown } from './ErrorBreakdown';
export { LatencyStats } from './LatencyStats';

export default SearchStatsDashboard;
